import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type {
  InjuryStatus,
  RosterIntelPlayer,
  RosterIntelResponse,
  StarterMatchup,
} from '@parlay-picker/shared/api/teamsApi';
import {
  mpgNoteText,
  PRIMARY_BENCH_MPG,
  ROSTER_UNAVAILABLE_TEXT,
  SECONDARY_BENCH_MPG,
  STARTER_MPG,
} from '@parlay-picker/shared/api/teamsApi';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { hasPlayerProps } from '@parlay-picker/shared/league';
import { getTeamLogo } from '@parlay-picker/shared/teams';
import { getPlayerHeadshotUrl } from '@views/picks/utils';
import { UsersRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { VSElectric } from './VSElectric';

// =============================================================================
// Sub-components
// =============================================================================

function InjuryBadge({ status }: { status?: InjuryStatus }) {
  if (!status) return null;
  const config: Record<InjuryStatus, { label: string; color: string; bg: string }> = {
    Out: { label: 'OUT', color: 'text-red-400', bg: 'bg-red-500/15' },
    'Out For Season': { label: 'OFS', color: 'text-red-300', bg: 'bg-red-400/10' },
    Doubtful: { label: 'D', color: 'text-orange-400', bg: 'bg-orange-500/15' },
    Questionable: { label: 'Q', color: 'text-yellow-400', bg: 'bg-yellow-500/15' },
    Probable: { label: 'PROB', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  };
  const { label, color, bg } = config[status];
  return (
    <span
      className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${bg} ${color} border-current/20`}
    >
      {label}
    </span>
  );
}

function PositionChip({ position }: { position: string }) {
  if (!position) return null;
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-theme-border text-theme-text-secondary font-mono">
      {position}
    </span>
  );
}

function PlayerHeadshot({ name, leagueKey }: { name: string; leagueKey: LeagueKey }) {
  const url = getPlayerHeadshotUrl(name, leagueKey);
  if (!url) return <div className="w-10 h-10 rounded-full bg-theme-surface-hover" />;
  return (
    <img
      src={url}
      alt={name}
      loading="lazy"
      decoding="async"
      className="w-10 h-10 rounded-full object-cover bg-theme-surface-hover"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

function RosterPlayerRow({
  player,
  isStarter,
  leagueKey,
}: {
  player: RosterIntelPlayer;
  isStarter: boolean;
  leagueKey: LeagueKey;
}) {
  return (
    <div className="flex items-center gap-2 py-2 px-4">
      {isStarter ? (
        <span className="text-yellow-400 text-xs w-3">★</span>
      ) : (
        <span className="w-3" />
      )}
      <PlayerHeadshot name={player.playerName} leagueKey={leagueKey} />
      <PositionChip position={player.position} />
      <span className="flex-1 text-sm text-theme-text truncate">{player.playerName}</span>
      <InjuryBadge status={player.injuryStatus} />
      <span className="text-xs text-theme-text-tertiary tabular-nums w-14 text-right">
        {player.mpg !== null ? `${player.mpg.toFixed(1)} mpg` : '—'}
      </span>
    </div>
  );
}

function StarterMatchupRow({
  matchup,
  leagueKey,
}: {
  matchup: StarterMatchup;
  leagueKey: LeagueKey;
}) {
  const { teamPlayer, opponentPlayer } = matchup;
  return (
    <div>
      <div className="flex items-center gap-1.5 py-2 px-4">
        {/* Team player — right-aligned toward center */}
        <div className="flex-1 flex items-center gap-1.5 justify-end min-w-0">
          <InjuryBadge status={teamPlayer.injuryStatus} />
          <span className="text-sm text-theme-text truncate text-right">
            {teamPlayer.playerName}
          </span>
          <PositionChip position={teamPlayer.position} />
          <PlayerHeadshot name={teamPlayer.playerName} leagueKey={leagueKey} />
        </div>

        {/* VS divider */}
        <span
          className="shrink-0 text-xs font-extrabold tracking-widest text-white px-1"
          style={{
            textShadow: '0 0 6px #fff, 0 0 14px #ffe066, 0 0 28px #ffcc00',
          }}
        >
          VS
        </span>

        {/* Opponent player — left-aligned from center */}
        <div className="flex-1 flex items-center gap-1.5 min-w-0">
          {opponentPlayer ? (
            <>
              <PlayerHeadshot name={opponentPlayer.playerName} leagueKey={leagueKey} />
              <PositionChip position={opponentPlayer.position} />
              <span className="text-sm text-theme-text truncate">{opponentPlayer.playerName}</span>
              <InjuryBadge status={opponentPlayer.injuryStatus} />
            </>
          ) : (
            <span className="text-sm text-theme-text-tertiary italic">TBA</span>
          )}
        </div>
      </div>
      {/* replacedNote ("X no longer on roster") omitted — replacement is already shown */}
    </div>
  );
}

function StarterMatchupHeader({
  teamAbbr,
  opponentAbbr,
  leagueKey,
}: {
  teamAbbr: string;
  opponentAbbr: string;
  leagueKey?: LeagueKey;
}) {
  const teamLogo = leagueKey ? getTeamLogo(teamAbbr, leagueKey) : null;
  const oppLogo = leagueKey ? getTeamLogo(opponentAbbr, leagueKey) : null;
  return (
    <div className="flex items-center justify-center gap-3 py-2">
      {teamLogo && (
        <img
          src={teamLogo}
          alt={teamAbbr}
          loading="lazy"
          decoding="async"
          className="w-6 h-6 object-contain"
        />
      )}
      <VSElectric size="sm" />
      {oppLogo && (
        <img
          src={oppLogo}
          alt={opponentAbbr}
          loading="lazy"
          decoding="async"
          className="w-6 h-6 object-contain"
        />
      )}
    </div>
  );
}

// =============================================================================
// LineupView — Pure content component (no modal/panel chrome)
// =============================================================================

export interface LineupViewProps {
  teamAbbr: string;
  opponentAbbr?: string;
  gameDate?: string;
  gameId?: string;
  leagueKey: LeagueKey;
}

export function LineupView({ teamAbbr, opponentAbbr, gameDate, leagueKey }: LineupViewProps) {
  const { authFetch } = useAuth();
  const [intel, setIntel] = useState<RosterIntelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIntel(null);

    const buildParams = (skipOpps: boolean) => {
      const params = new URLSearchParams();
      if (gameDate) params.set('gameDate', gameDate);
      if (opponentAbbr) params.set('opponentAbbr', opponentAbbr);
      if (skipOpps) params.set('skipOpportunities', 'true');
      if (leagueKey) params.set('leagueKey', leagueKey);
      return params.toString();
    };

    // Phase 1: Fast fetch without opportunity analysis
    authFetch(`${API_BASE_URL}/api/teams/${teamAbbr}/intel?${buildParams(true)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load team intel');
        return res.json() as Promise<RosterIntelResponse>;
      })
      .then((data) => {
        if (cancelled) return;
        setIntel(data);
        setLoading(false);

        // Prefetch opponent data so server cache is warm when user switches teams
        if (opponentAbbr) {
          authFetch(`${API_BASE_URL}/api/teams/${opponentAbbr}/intel?${buildParams(true)}`).catch(
            () => {},
          );
        }

        // Phase 2: Full fetch with opportunity analysis (background)
        setOpportunitiesLoading(true);
        return authFetch(`${API_BASE_URL}/api/teams/${teamAbbr}/intel?${buildParams(false)}`)
          .then(async (res) => {
            if (!res.ok) return;
            return res.json() as Promise<RosterIntelResponse>;
          })
          .then((fullData) => {
            if (cancelled || !fullData) return;
            setIntel(fullData);
          })
          .catch(() => {
            // Phase 2 failure is non-critical — opportunities just stay empty
          })
          .finally(() => {
            if (!cancelled) setOpportunitiesLoading(false);
          });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load team intel');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [teamAbbr, gameDate, opponentAbbr, authFetch, leagueKey]);

  // Tier groups + injured players
  const { starters, primaryBench, secondaryBench, deepBench, injuredPlayers } = useMemo(() => {
    const roster = intel?.roster ?? [];
    return {
      starters: roster.filter((p) => (p.mpg ?? 0) >= STARTER_MPG),
      primaryBench: roster.filter(
        (p) => (p.mpg ?? 0) >= PRIMARY_BENCH_MPG && (p.mpg ?? 0) < STARTER_MPG,
      ),
      secondaryBench: roster.filter(
        (p) => (p.mpg ?? 0) >= SECONDARY_BENCH_MPG && (p.mpg ?? 0) < PRIMARY_BENCH_MPG,
      ),
      deepBench: roster.filter((p) => (p.mpg ?? 0) < SECONDARY_BENCH_MPG),
      injuredPlayers: roster
        .filter((p) => p.injuryStatus && p.injuryStatus !== 'Probable')
        .sort((a, b) => {
          const order: Record<string, number> = {
            Out: 0,
            'Out For Season': 0,
            Doubtful: 1,
            Questionable: 2,
            Probable: 3,
          };
          const statusDiff =
            order[a.injuryStatus ?? 'Probable'] - order[b.injuryStatus ?? 'Probable'];
          if (statusDiff !== 0) return statusDiff;
          return (b.mpg ?? 0) - (a.mpg ?? 0);
        }),
    };
  }, [intel?.roster]);

  // Starters from projected lineup (override tier classification)
  const lineupStarterIds = new Set(
    intel?.projectedLineup?.filter((s) => s.isStarter).map((s) => s.player.playerId) ?? [],
  );

  const isStarterFn = (p: RosterIntelPlayer) =>
    lineupStarterIds.size > 0 ? lineupStarterIds.has(p.playerId) : (p.mpg ?? 0) >= STARTER_MPG;

  // Guard — roster intel requires player props (professional leagues only)
  if (leagueKey && !hasPlayerProps(leagueKey)) {
    return (
      <div className="p-8 text-center space-y-2">
        <UsersRound className="w-8 h-8 text-theme-text-tertiary mx-auto" />
        <p className="text-sm text-theme-text-secondary">
          Roster intel is available for leagues with player props only
        </p>
        <p className="text-xs text-theme-text-tertiary">
          College roster data is not currently supported
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 rounded bg-theme-border animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-theme-text-secondary text-sm">{error}</div>;
  }

  if (!intel) return null;

  return (
    <>
      {intel.roster.length > 0 && (
        <div className="px-4 pt-2 pb-0.5 text-xs text-theme-text-tertiary italic">
          {mpgNoteText(intel.mpgSeason)}
        </div>
      )}
      {/* Starter Matchups or Projected Starters */}
      {intel.starterMatchups && intel.starterMatchups.length > 0 ? (
        <section className="border-b border-theme-border">
          {opponentAbbr && (
            <StarterMatchupHeader
              teamAbbr={teamAbbr}
              opponentAbbr={opponentAbbr}
              leagueKey={leagueKey}
            />
          )}
          <div className="px-4 py-1 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide text-center">
            Starting Matchups
          </div>
          {intel.starterMatchups.map((m) => (
            <StarterMatchupRow key={m.slotNumber} matchup={m} leagueKey={leagueKey} />
          ))}
        </section>
      ) : intel.projectedLineup !== null ? (
        <section className="border-b border-theme-border">
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Tonight's Projected Starters
          </div>
          {intel.projectedLineup.length === 0 ? (
            <div className="px-4 pb-3 text-sm text-theme-text-secondary">Lineup TBA</div>
          ) : (
            intel.projectedLineup
              .filter((s) => s.isStarter)
              .map((slot) => (
                <div key={slot.slot} className="flex items-center gap-2 py-2 px-4">
                  <PlayerHeadshot name={slot.player.playerName} leagueKey={leagueKey} />
                  <PositionChip position={slot.player.position} />
                  <span className="flex-1 text-sm text-theme-text truncate">
                    {slot.player.playerName}
                  </span>
                  <InjuryBadge status={slot.player.injuryStatus} />
                </div>
              ))
          )}
        </section>
      ) : null}

      {/* Injuries */}
      {injuredPlayers.length > 0 && (
        <section className="border-b border-theme-border">
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Injuries
          </div>
          {injuredPlayers.map((player) => (
            <div key={player.playerId} className="px-4 py-2.5">
              <div className="flex items-center gap-2">
                <PlayerHeadshot name={player.playerName} leagueKey={leagueKey} />
                <PositionChip position={player.position} />
                <span className="flex-1 text-sm font-medium text-theme-text">
                  {player.playerName}
                  {player.injuryDescription && (
                    <span className="text-xs font-normal text-theme-text-tertiary ml-1">
                      · {player.injuryDescription}
                    </span>
                  )}
                  {player.gamesMissed != null && player.gamesMissed > 0 && (
                    <span className="text-xs font-normal text-theme-text-tertiary ml-1">
                      · missed {player.gamesMissed} games
                    </span>
                  )}
                </span>
                <InjuryBadge status={player.injuryStatus} />
                <span className="text-xs text-theme-text-tertiary tabular-nums w-14 text-right">
                  {player.mpg !== null ? `${player.mpg.toFixed(1)} mpg` : ''}
                </span>
              </div>
              {/* Beneficiary rows for OUT players */}
              {(player.injuryStatus === 'Out' || player.injuryStatus === 'Out For Season') && (
                <div className="ml-11 mt-1.5 space-y-0.5">
                  {opportunitiesLoading ? (
                    <div className="text-xs text-theme-text-tertiary animate-pulse">
                      Loading beneficiary data...
                    </div>
                  ) : player.opportunities && player.opportunities.length > 0 ? (
                    player.opportunities.map((opp) => {
                      const diffs = Object.entries(opp.statDifferentials)
                        .filter(([, v]) => Math.abs(v) >= 0.5)
                        .map(([k, v]) => ({
                          label: k,
                          value: `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
                          positive: v > 0,
                        }));
                      return (
                        <div key={opp.beneficiaryId} className="text-xs text-theme-text-secondary">
                          <span className="text-theme-text-tertiary mr-1">→</span>
                          <span className="font-medium">{opp.beneficiaryName}:</span>
                          {diffs.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 flex-wrap">
                              {diffs.map((d, i) => (
                                <span key={d.label} className="inline-flex items-center">
                                  {i > 0 && (
                                    <span className="mx-1 text-theme-text-tertiary">|</span>
                                  )}
                                  <span
                                    className={d.positive ? 'text-emerald-400' : 'text-red-400'}
                                  >
                                    {d.value}
                                  </span>
                                  <span className="text-theme-text-tertiary ml-0.5">{d.label}</span>
                                </span>
                              ))}
                            </span>
                          )}
                          <span className="ml-1.5 text-theme-text-tertiary">
                            ({opp.sampleSize} games ·{' '}
                            {opp.confidence.charAt(0).toUpperCase() + opp.confidence.slice(1)})
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-xs text-theme-text-tertiary">
                      Redistribution data unavailable
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {/* Roster Tiers */}
      {starters.length > 0 && (
        <section className="border-b border-theme-border">
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Starters (25+ mpg)
          </div>
          {starters.map((p) => (
            <RosterPlayerRow
              key={p.playerId}
              player={p}
              isStarter={isStarterFn(p)}
              leagueKey={leagueKey}
            />
          ))}
        </section>
      )}

      {primaryBench.length > 0 && (
        <section className="border-b border-theme-border">
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Primary Bench (15–25 mpg)
          </div>
          {primaryBench.map((p) => (
            <RosterPlayerRow key={p.playerId} player={p} isStarter={false} leagueKey={leagueKey} />
          ))}
        </section>
      )}

      {secondaryBench.length > 0 && (
        <section className="border-b border-theme-border">
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Secondary Bench (8–15 mpg)
          </div>
          {secondaryBench.map((p) => (
            <RosterPlayerRow key={p.playerId} player={p} isStarter={false} leagueKey={leagueKey} />
          ))}
        </section>
      )}

      {deepBench.length > 0 && (
        <section>
          <div className="px-4 py-2.5 text-xs font-semibold text-theme-text-tertiary uppercase tracking-wide">
            Deep Bench (&lt;8 mpg)
          </div>
          {deepBench.map((p) => (
            <div key={p.playerId} className="flex items-center gap-2 py-1.5 px-4 opacity-50">
              <span className="w-3" />
              <PlayerHeadshot name={p.playerName} leagueKey={leagueKey} />
              <PositionChip position={p.position} />
              <span className="flex-1 text-xs text-theme-text-secondary truncate">
                {p.playerName}
              </span>
              <span className="text-xs text-theme-text-tertiary tabular-nums">
                {p.mpg !== null ? `${p.mpg.toFixed(1)} mpg` : '—'}
              </span>
            </div>
          ))}
        </section>
      )}

      {/* Empty roster */}
      {intel.roster.length === 0 && (
        <div className="p-6 text-center text-sm text-theme-text-secondary">
          {ROSTER_UNAVAILABLE_TEXT}
        </div>
      )}
    </>
  );
}
